import { Input, Label, cn } from "@zaphub/ui";
import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from "react";

interface CampoProps extends InputHTMLAttributes<HTMLInputElement> {
  rotulo: string;
  erro?: string;
  dica?: ReactNode;
}

function descricaoDe(campoId: string, erro?: string, dica?: ReactNode): string | undefined {
  return [erro ? `${campoId}-erro` : null, dica ? `${campoId}-dica` : null].filter(Boolean).join(" ") || undefined;
}

function Mensagens({ campoId, erro, dica }: { campoId: string; erro?: string; dica?: ReactNode }) {
  return (
    <>
      {dica && (
        <div id={`${campoId}-dica`} className="text-xs text-muted-foreground">
          {dica}
        </div>
      )}
      {erro && (
        <p id={`${campoId}-erro`} role="alert" className="text-xs font-medium text-destructive">
          {erro}
        </p>
      )}
    </>
  );
}

/** Rótulo visível + input + erro inline, com `aria-invalid` e `aria-describedby` ligados. */
export const Campo = forwardRef<HTMLInputElement, CampoProps>(function Campo(
  { rotulo, erro, dica, id, className, ...props },
  ref,
) {
  const auto = useId();
  const campoId = id ?? auto;
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={campoId}>{rotulo}</Label>
      <Input
        ref={ref}
        id={campoId}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descricaoDe(campoId, erro, dica)}
        className={cn("h-11 sm:h-10", className)}
        {...props}
      />
      <Mensagens campoId={campoId} erro={erro} dica={dica} />
    </div>
  );
});

/** Campo de senha com botão de mostrar/ocultar (alvo de 44px no celular). */
export const CampoSenha = forwardRef<HTMLInputElement, Omit<CampoProps, "type">>(function CampoSenha(
  { rotulo, erro, dica, id, ...props },
  ref,
) {
  const auto = useId();
  const campoId = id ?? auto;
  const [visivel, setVisivel] = useState(false);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={campoId}>{rotulo}</Label>
      <div className="relative">
        <Input
          ref={ref}
          id={campoId}
          type={visivel ? "text" : "password"}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descricaoDe(campoId, erro, dica)}
          className="h-11 pr-12 sm:h-10"
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisivel((v) => !v)}
          aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
          aria-pressed={visivel}
          className="absolute right-0 top-0 grid size-11 place-items-center rounded-md text-muted-foreground hover:text-foreground sm:size-10"
        >
          {visivel ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      <Mensagens campoId={campoId} erro={erro} dica={dica} />
    </div>
  );
});
